/**
 * components/CountryPicker.tsx — E.164 dial-code picker.
 *
 * Compact button showing the flag + `+dial`; opens a dropdown with a
 * search input and the full country list. Keyboard-accessible:
 *
 *   * Enter/Space on the trigger opens the panel.
 *   * Arrow keys navigate the list (list is a listbox with roving tabindex).
 *   * Enter selects; Escape closes without selecting.
 *
 * Rendered adjacent to the phone number Field so the user reads
 * `[+1 US ▾] [ ______ ]` as a single conceptual E.164 input.
 *
 * No portal — the dropdown positions relative to the button. On
 * mobile-narrow the panel goes full-width below the button (see
 * .panel media query).
 */

import { useEffect, useMemo, useRef, useState } from "react";
import {
  COUNTRIES,
  filterCountries,
  type Country,
} from "../data/countries";
import styles from "./CountryPicker.module.css";

interface CountryPickerProps {
  value: Country;
  onChange: (next: Country) => void;
  disabled?: boolean;
  /** Optional id for the trigger button; auto-generated if omitted. */
  id?: string;
  /** aria-label for the trigger. Default: "Country code". */
  ariaLabel?: string;
}

export function CountryPicker({
  value,
  onChange,
  disabled = false,
  id,
  ariaLabel = "Country code",
}: CountryPickerProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [focusedIdx, setFocusedIdx] = useState(0);

  const wrapRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);

  const results = useMemo(() => filterCountries(query), [query]);

  // Focus search input when the panel opens.
  useEffect(() => {
    if (open) {
      // Next tick so the input has mounted.
      const t = setTimeout(() => inputRef.current?.focus(), 0);
      return () => clearTimeout(t);
    }
    setQuery("");
    setFocusedIdx(0);
    return undefined;
  }, [open]);

  // Reset focused index when results change (e.g. typing filter).
  useEffect(() => {
    setFocusedIdx(0);
  }, [query]);

  // Click-outside to close.
  useEffect(() => {
    if (!open) return undefined;
    function onDoc(e: MouseEvent) {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  // Keep focused item scrolled into view.
  useEffect(() => {
    if (!open) return;
    const el = listRef.current?.querySelector<HTMLElement>(
      `[data-idx="${focusedIdx}"]`,
    );
    el?.scrollIntoView({ block: "nearest" });
  }, [focusedIdx, open]);

  function pick(country: Country) {
    onChange(country);
    setOpen(false);
  }

  function onListKeyDown(e: React.KeyboardEvent) {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setFocusedIdx((i) => Math.min(i + 1, results.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setFocusedIdx((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      const c = results[focusedIdx];
      if (c) pick(c);
    } else if (e.key === "Escape") {
      e.preventDefault();
      setOpen(false);
    } else if (e.key === "Home") {
      e.preventDefault();
      setFocusedIdx(0);
    } else if (e.key === "End") {
      e.preventDefault();
      setFocusedIdx(results.length - 1);
    }
  }

  return (
    <div className={styles.wrap} ref={wrapRef}>
      <button
        type="button"
        id={id}
        className={styles.trigger}
        onClick={() => !disabled && setOpen((o) => !o)}
        aria-label={`${ariaLabel}: ${value.name}, +${value.dial}`}
        aria-haspopup="listbox"
        aria-expanded={open}
        disabled={disabled}
      >
        <span aria-hidden className={styles.flag}>{value.flag}</span>
        <span className={styles.dial}>+{value.dial}</span>
        <svg
          aria-hidden
          className={styles.chevron}
          viewBox="0 0 20 20"
          width="16"
          height="16"
        >
          <path
            d="M5 8l5 5 5-5"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.75"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>
      {open && (
        <div className={styles.panel} role="dialog" aria-label="Choose country">
          <input
            ref={inputRef}
            className={styles.search}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={onListKeyDown}
            placeholder="Search country or code"
            aria-label="Search country"
            autoComplete="off"
          />
          <ul
            ref={listRef}
            className={styles.list}
            role="listbox"
            tabIndex={-1}
            aria-activedescendant={
              results[focusedIdx]
                ? `country-opt-${results[focusedIdx].code}`
                : undefined
            }
            onKeyDown={onListKeyDown}
          >
            {results.length === 0 && (
              <li className={styles.empty}>
                No match — type the full +code above.
              </li>
            )}
            {results.map((c, i) => (
              <li
                id={`country-opt-${c.code}`}
                key={c.code}
                data-idx={i}
                role="option"
                aria-selected={c.code === value.code}
                className={
                  i === focusedIdx
                    ? `${styles.item} ${styles.itemFocused}`
                    : styles.item
                }
                onMouseEnter={() => setFocusedIdx(i)}
                onClick={() => pick(c)}
              >
                <span aria-hidden className={styles.flag}>{c.flag}</span>
                <span className={styles.name}>{c.name}</span>
                <span className={styles.dialSm}>+{c.dial}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

/**
 * Utility: strip formatting characters from an E.164-ish string.
 * Keeps digits only. Callers concatenate `+<dial><digits>` to build
 * the wire format.
 */
export function digitsOnly(input: string): string {
  return input.replace(/\D+/g, "");
}

/** Compose the wire-format E.164 from picker + digits. */
export function toE164(country: Country, digits: string): string {
  return `+${country.dial}${digitsOnly(digits)}`;
}

// Re-export for callers that want to render country info elsewhere.
export { COUNTRIES };
